(() => {
class SiswayMenuSurface {
  constructor(root) {
    this.root = root;
    this.items = new Set();
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.surface = document.createElement('div');
    this.surface.className = 'sisway-menu-surface';
    this.surface.hidden = true;
    root.append(this.surface);
    this.surface.addEventListener('pointerenter', () => {
      this.cancelClose();
      if (!this.active && this.closingItem && !this.surface.hidden) this.open(this.closingItem);
    }, options);
    root.addEventListener('pointerenter', () => this.cancelClose(), options);
    const closeLeaf = event => {
      const link = event.target.closest('.m-menu__link--main');
      if (!link || !root.contains(link)) return;
      const li = link.closest('li');
      if (!li.querySelector('sisway-nav-item') && !li.hasAttribute('data-sisway-managed')) this.close();
    };
    root.addEventListener('pointerover', event => { if (event.pointerType === 'mouse') closeLeaf(event); }, options);
    root.addEventListener('focusin', closeLeaf, options);
    root.addEventListener('pointerleave', event => {
      if (event.pointerType === 'mouse') this.scheduleClose();
    }, options);
    root.addEventListener('focusout', event => {
      if (!root.contains(event.relatedTarget)) this.close();
    }, options);
    root.addEventListener('keydown', event => this.onKeydown(event), options);
    document.addEventListener('pointerdown', event => {
      if (!root.contains(event.target)) this.close();
    }, options);
    this.panelObserver = new ResizeObserver(() => this.resize());
    this.layoutObserver = new ResizeObserver(entries => {
      const width = entries[0].contentRect.width;
      if (width === this.width) return;
      this.width = width;
      this.layout();
    });
    this.layoutObserver.observe(root.closest('m-header') || root);
    const mobile = root.closest('m-header')?.querySelector('.m-header__mobile');
    if (mobile) {
      const toggles = [...mobile.querySelectorAll('[data-toggle-submenu]')];
      toggles.forEach(toggle => {
        const panel = toggle.closest('li')?.querySelector('.m-megamenu-mobile');
        if (!panel) return;
        panel.id ||= `SISWAYMobile-${crypto.randomUUID()}`;
        toggle.setAttribute('aria-controls', panel.id);
        toggle.setAttribute('aria-expanded', String(panel.classList.contains('open')));
        if (toggle.tagName === 'SPAN') {
          toggle.setAttribute('role', 'button');
          toggle.setAttribute('aria-label', toggle.closest('li').querySelector('a')?.textContent.trim() || '');
          toggle.tabIndex = 0;
          toggle.addEventListener('keydown', event => {
            if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); toggle.click(); }
          }, options);
        }
      });
      mobile.addEventListener('click', () => requestAnimationFrame(() => {
        toggles.forEach(toggle => {
          const panel = document.getElementById(toggle.getAttribute('aria-controls'));
          if (panel) toggle.setAttribute('aria-expanded', String(panel.classList.contains('open')));
        });
      }), options);
    }
    document.fonts.ready.then(() => { if (!this.controller.signal.aborted) this.layout(); });
    window.addEventListener('resize', () => this.resize(), options);
  }
  duration(value) { return matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : value; }
  cancelClose() { clearTimeout(this.closeTimer); }
  scheduleClose() {
    this.cancelClose();
    this.closeTimer = setTimeout(() => {
      if (!this.root.contains(document.activeElement)) this.close(true);
    }, 250);
  }
  add(item) {
    this.items.add(item);
    this.surface.append(item.panel);
    this.panelObserver.observe(item.panel);
  }
  state(item, open) {
    item.opened = open;
    item.toggle.setAttribute('aria-expanded', String(open));
    item.parentElement.classList.toggle('is-open', open);
    item.panel.inert = !open;
    item.panel.setAttribute('aria-hidden', String(!open));
  }
  fade(item, open, duration) {
    const panel = item.panel;
    const opacity = panel.hidden ? 0 : Number(getComputedStyle(panel).opacity);
    panel.motion?.cancel();
    panel.hidden = false;
    panel.style.zIndex = open ? '1' : '0';
    panel.motion = panel.animate([{ opacity }, { opacity: open ? 1 : 0 }], {
      duration: this.duration(duration), easing: 'cubic-bezier(.22,1,.36,1)', fill: 'both'
    });
    if (!open) panel.motion.onfinish = () => { if (!item.opened) panel.hidden = true; };
  }
  enhance(owner) {
    const root = owner.closest('.m-header__desktop');
    const managed = [];
    owner.querySelectorAll('.m-menu > li').forEach((li, index) => {
      if (li.querySelector('sisway-nav-item') || li.hasAttribute('data-sisway-managed')) return;
      const panel = li.querySelector(':scope > .m-mega-menu');
      const toggle = li.querySelector(':scope > .m-menu__link--main');
      if (!panel || !toggle) return;
      panel.id ||= `SISWAY-${crypto.randomUUID()}`;
      li.dataset.siswayManaged = '';
      li.dataset.siswayPanel = panel.id;
      panel.dataset.siswayOwner = '';
      panel.classList.add('sisway-nav-panel', 'sisway-standard-panel');
      ['m-menu__item--mega', 'm-menu__item--dropdown', 'm-menu__item--has-submenu'].forEach(name => {
        if (li.classList.contains(name)) panel.classList.add(name);
      });
      const item = { panel, toggle, parentElement: li, append: node => li.append(node), opened: false, controller: new AbortController() };
      toggle.setAttribute('aria-controls', panel.id);
      toggle.setAttribute('aria-expanded', 'false');
      panel.hidden = true;
      this.state(item, false);
      this.add(item);
      managed.push(item);
      const options = { signal: item.controller.signal };
      li.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') this.open(item); }, options);
      li.addEventListener('focusin', () => { if (toggle.matches(':focus-visible')) this.open(item); }, options);
      toggle.addEventListener('click', event => {
        if (toggle.getAttribute('href') === '#' || event.target.closest('.m-menu__arrow')) {
          event.preventDefault(); this.active === item ? this.close() : this.open(item);
        }
      }, options);
      toggle.addEventListener('keydown', event => {
        if (event.key === 'ArrowDown') {
          event.preventDefault(); this.open(item); panel.querySelector('a, button')?.focus();
        } else if (event.key === ' ') {
          event.preventDefault(); this.active === item ? this.close() : this.open(item);
        }
      }, options);
    });
    return managed;
  }
  open(item) {
    this.cancelClose();
    if (this.active === item) return;
    const first = this.surface.hidden;
    const opacity = first ? 0 : Number(getComputedStyle(this.surface).opacity);
    const transform = first ? 'translateY(4px)' : getComputedStyle(this.surface).transform;
    this.surface.motion?.cancel();
    let outgoing;
    let strongest = 0;
    this.items.forEach(other => {
      const value = other.panel.hidden ? 0 : Number(getComputedStyle(other.panel).opacity);
      other.panel.motion?.cancel();
      other.panel.style.opacity = String(value);
      if (value > strongest) { strongest = value; outgoing = other; }
      this.state(other, other === item);
    });
    const version = this.version = (this.version || 0) + 1;
    this.active = item;
    this.surface.classList.toggle("is-about", item.panel.hasAttribute("data-about-panel"));
    this.surface.hidden = false;
    this.items.forEach(other => { other.panel.hidden = other !== item && other !== outgoing; });
    const reveal = () => {
      if (this.version !== version || this.active !== item) return;
      this.items.forEach(other => { if (other !== item) other.panel.hidden = true; });
      this.fade(item, true, first ? 0 : 280);
    };
    if (!first && outgoing && outgoing !== item && this.duration(380)) {
      item.panel.hidden = false;
      item.panel.style.opacity = '0';
      this.fade(outgoing, false, 100);
      outgoing.panel.motion.onfinish = reveal;
    } else {
      if (outgoing === item) this.fade(item, true, first ? 0 : 380);
      else { item.panel.style.opacity = '0'; reveal(); }
    }
    this.resize();
    requestAnimationFrame(() => {
      if (this.active !== item) return;
      item.panel.querySelectorAll('.swiper-container').forEach(node => node.swiper?.update());
    });
    if (first || opacity < 1) {
      this.surface.motion = this.surface.animate([{ opacity, transform }, { opacity: 1, transform: 'translateY(0)' }], {
        duration: this.duration(item.panel.querySelector('.sisway-shop-showcase') ? 320 : 450), easing: 'cubic-bezier(.22,1,.36,1)', fill: 'both'
      });
    }
  }
  close(restoreOnEnter = false) {
    this.cancelClose();
    if (!this.active) return;
    this.closingItem = restoreOnEnter ? this.active : null;
    this.active = null;
    this.version = (this.version || 0) + 1;
    this.items.forEach(item => this.state(item, false));
    const opacity = getComputedStyle(this.surface).opacity;
    const transform = getComputedStyle(this.surface).transform;
    this.surface.motion?.cancel();
    this.surface.motion = this.surface.animate([{ opacity, transform }, { opacity: 0, transform: 'translateY(4px)' }], {
      duration: this.duration(350), easing: 'cubic-bezier(.22,1,.36,1)', fill: 'both'
    });
    this.surface.motion.onfinish = () => {
      if (this.active) return;
      this.surface.hidden = true;
      this.items.forEach(item => { item.panel.hidden = true; item.panel.motion?.cancel(); });
    };
  }
  resize() {
    if (!this.active) return;
    this.surface.style.maxHeight = `${Math.max(120, window.innerHeight - this.root.getBoundingClientRect().bottom - 12)}px`;
    this.surface.style.height = `${Math.ceil(this.active.panel.getBoundingClientRect().height)}px`;
  }
  layout() {
    const header = this.root.closest('m-header');
    const inner = this.root.querySelector('.m-header__inner');
    if (!header || !inner || !this.root.classList.contains('logo-left-menu-center')) return;
    header.classList.remove('sisway-header-compact');
    if (window.innerWidth >= 1024) {
      const menu = inner.querySelector('.m-menu');
      const left = inner.querySelector('.m-header__left');
      const right = inner.querySelector('.m-header__right');
      if (menu && left && right) {
        const gap = parseFloat(getComputedStyle(inner).columnGap) || 0;
        const required = menu.scrollWidth + left.scrollWidth + right.scrollWidth + gap * 2;
        header.classList.toggle('sisway-header-compact', required > inner.clientWidth);
      }
    }
    if (getComputedStyle(this.root).display === 'none') this.close();
    if (typeof window._shh === 'function') window._shh();
    const nativeHeader = header;
    if (nativeHeader.domNodes?.headerWrapper) nativeHeader.headerHeight = nativeHeader.domNodes.headerWrapper.offsetHeight;
    this.resize();
  }
  onKeydown(event) {
    const item = this.active;
    if (!item) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      item.toggle.focus();
      this.close();
    }
    if (event.key !== 'Tab') return;
    const links = [...item.panel.querySelectorAll('a, button, [tabindex="0"]')].filter(e => !e.closest('[hidden], [inert]') && e.tabIndex >= 0);
    if (!event.shiftKey && event.target === item.toggle && links.length) {
      event.preventDefault(); links[0].focus();
    } else if (event.shiftKey && event.target === links[0]) {
      event.preventDefault(); item.toggle.focus();
    } else if (!event.shiftKey && event.target === links.at(-1)) {
      const next = item.parentElement.nextElementSibling?.querySelector('a, button');
      if (next) { event.preventDefault(); this.close(); next.focus(); }
    }
  }
  remove(item) {
    item.controller?.abort();
    this.panelObserver.unobserve(item.panel);
    item.panel.motion?.cancel();
    this.state(item, false);
    if (item.parentElement.hasAttribute('data-sisway-managed')) {
      delete item.parentElement.dataset.siswayManaged;
      delete item.parentElement.dataset.siswayPanel;
    }
    this.items.delete(item);
    item.append(item.panel);
    item.panel.hidden = true;
    if (this.active === item) this.close();
    if (this.closingItem === item) this.closingItem = null;
    if (!this.items.size) {
      this.cancelClose();
      this.controller.abort();
      this.panelObserver.disconnect();
      this.layoutObserver.disconnect();
      this.surface.motion?.cancel();
      this.surface.remove();
      delete this.root.siswaySurface;
    }
  }
}

if (!customElements.get('sisway-nav-item')) {
  customElements.define('sisway-nav-item', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.toggle = this.querySelector('[data-menu-toggle]');
      this.panel = this.querySelector('[data-menu-panel]');
      if (!this.toggle || !this.panel) return;
      const root = this.closest('.m-header__desktop');
      if (!root) return;
      this.group = root.siswaySurface ||= new SiswayMenuSurface(root);
      this.opened = false;
      this.panel.inert = true;
      this.group.add(this);
      this.addEventListener('pointerenter', event => {
        if (event.pointerType === 'mouse') this.setOpen(true);
      }, options);
      this.addEventListener('focusin', event => {
        if (event.target !== this.toggle) this.setOpen(true);
      }, options);
      this.toggle.addEventListener('click', () => this.setOpen(!this.opened), options);
      this.querySelector('.m-menu__link--main')?.addEventListener('click', event => {
        const href = event.currentTarget.getAttribute('href');
        if (!href || href === '#') { event.preventDefault(); this.setOpen(!this.opened); }
      }, options);
      this.addEventListener('keydown', event => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          this.setOpen(true);
          this.panel.querySelector('button, a')?.focus();
        }
      }, options);
    }
    setOpen(open) { if (open) this.group.open(this); else if (this.group.active === this) this.group.close(); }
    disconnectedCallback() { this.controller?.abort(); this.group?.remove(this); }
  });
}
if (!customElements.get('sisway-navigation')) {
  customElements.define('sisway-navigation', class extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected) return;
        const root = this.closest('.m-header__desktop');
        if (!root || (!root.siswaySurface && !this.querySelector('.m-menu > li > .m-mega-menu'))) return;
        this.group = root.siswaySurface ||= new SiswayMenuSurface(root);
        this.managed = this.group.enhance(this);
      });
    }
    disconnectedCallback() { this.managed?.forEach(item => this.group.remove(item)); this.managed = []; }
  });
}
if (!customElements.get('sisway-shop-menu')) {
  customElements.define('sisway-shop-menu', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.tabs = [...this.querySelectorAll('[data-shop-category]')];
      this.panels = [...this.querySelectorAll('[data-shop-products]')];
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse' && !this.classList.contains('sisway-shop-showcase--mobile')) this.select(tab);
        }, options);
        tab.addEventListener('click', () => this.select(tab), options);
        tab.addEventListener('focus', () => this.select(tab), options);
        tab.addEventListener('keydown', (event) => {
          let next;
          if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') next = (index + this.tabs.length - 1) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          this.tabs[next].focus();
        }, options);
      });
      this.addEventListener('keydown', event => {
        const accordion = this.closest('sisway-accordion');
        if (event.key === 'Escape' && accordion?.querySelector('details[open]')) {
          event.preventDefault();
          event.stopPropagation();
          const summary = accordion.querySelector('summary');
          summary.click();
          summary.focus();
        }
      }, options);
      document.addEventListener('shopify:block:select', event => {
        const source = event.target.closest('sisway-shop-menu');
        const header = this.closest('m-header');
        const surface = this.closest('.m-header__desktop, .m-header__mobile');
        if (!source || source.closest('m-header') !== header || !surface || getComputedStyle(surface).display === 'none') return;
        const region = event.target.closest('[data-shop-products], [data-shop-companion]');
        const key = region?.dataset.shopProducts || region?.dataset.shopCompanion;
        const tab = this.tabs.find(tab => tab.dataset.shopCategory === key);
        if (tab) this.select(tab);
        const group = this.closest('.m-header__desktop')?.siswaySurface;
        const owner = group && [...group.items].find(item => item.panel.contains(this));
        if (owner) group.open(owner);
        const accordion = this.closest('sisway-accordion');
        if (accordion) {
          const drawer = this.closest('.m-menu-drawer');
          if (drawer && !drawer.classList.contains('open')) surface.querySelector('.m-hamburger-box')?.click();
          const summary = accordion.querySelector('summary');
          if (summary.getAttribute('aria-expanded') !== 'true') summary.click();
        }
      }, options);
      this.panels.forEach((panel) => {
        const cards = [...panel.querySelectorAll('[data-feature-terms]')];
        const buttons = [...panel.querySelectorAll('[data-product-feature]')];
        buttons.forEach((button) => {
          button.hidden = !cards.some((card) => this.matchesFeature(card, button.dataset.productFeature));
          button.addEventListener('click', () => {
            button.setAttribute('aria-pressed', String(button.getAttribute('aria-pressed') !== 'true'));
            this.filter(panel);
          }, options);
        });
        const filters = panel.querySelector('.sisway-shop-menu__filters');
        if (filters) filters.hidden = !buttons.some((button) => !button.hidden);
        this.filter(panel);
      });
      this.select(this.tabs.find(tab => tab.getAttribute('aria-selected') === 'true') || this.tabs[0]);
    }
    select(selected) {
      if (!selected) return;
      this.tabs.forEach((tab) => {
        const active = tab === selected;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      if (this.selected === selected) return;
      this.selected = selected;
      // Each instance owns its state; desktop and drawer IDs are distinct.
      this.querySelectorAll('[data-shop-products], [data-shop-companion]').forEach(region => {
        region.motion?.cancel();
        const key = region.dataset.shopProducts || region.dataset.shopCompanion;
        const active = key === selected.dataset.shopCategory;
        region.inert = !active;
        region.hidden = !active;
        region.setAttribute('aria-hidden', String(!active));
      });
      // Let content shrink when changing category, including after viewport changes.
      this.querySelector('.sisway-shop-menu__content')?.style.removeProperty('min-height');
    }
    matchesFeature(card, feature) {
      const terms = (card.dataset.featureTerms || '').toLowerCase();
      const aliases = {
        'low-temp': ['low-temp', 'low temp', 'low temperature'],
        heated: ['heated', 'self-heating', 'self heating'],
        bluetooth: ['bluetooth'],
        switch: ['switch'],
      };
      return (aliases[feature] || []).some((term) => terms.includes(term));
    }
    filter(panel) {
      const cards = [...panel.querySelectorAll('[data-feature-terms]')];
      const features = [...panel.querySelectorAll('[data-product-feature]')]
        .filter((button) => button.getAttribute('aria-pressed') === 'true')
        .map((button) => button.dataset.productFeature);
      cards.forEach((card) => { card.hidden = !features.every((feature) => this.matchesFeature(card, feature)); });
      panel.querySelector('[data-shop-empty]').hidden = cards.some((card) => !card.hidden);
    }
    disconnectedCallback() {
      this.panels?.forEach(panel => { clearTimeout(panel.hideTimer); panel.motion?.cancel(); panel.querySelectorAll('.sisway-shop-card').forEach(card => card.motion?.cancel()); });
      this.controller?.abort();
    }
  });
}

if (!customElements.get('sisway-accordion')) {
  customElements.define('sisway-accordion', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.details = this.querySelector('details');
      this.summary = this.details.querySelector('summary');
      this.summary.setAttribute('aria-expanded', String(this.details.open));
      this.summary.addEventListener('click', event => {
        event.preventDefault();
        const open = this.summary.getAttribute('aria-expanded') !== 'true';
        this.summary.setAttribute('aria-expanded', String(open));
        const start = this.details.getBoundingClientRect().height;
        this.animation?.cancel();
        this.details.open = true;
        const end = open ? this.details.scrollHeight : this.summary.getBoundingClientRect().height;
        this.details.querySelector('[data-accordion-content]').inert = !open;
        this.animation = this.details.animate([{ height: `${start}px` }, { height: `${end}px` }], {
          duration: matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 280,
          easing: 'cubic-bezier(.22,1,.36,1)'
        });
        this.animation.onfinish = () => { this.details.open = open; };
      }, { signal: this.controller.signal });
    }
    disconnectedCallback() { this.animation?.cancel(); this.controller?.abort(); }
  });
}

if (!customElements.get('sisway-about-menu')) customElements.define('sisway-about-menu', class extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const options = {signal:this.controller.signal};
    this.tabs = [...this.querySelectorAll('[data-about-topic]')];
    this.panels = [...this.querySelectorAll('[data-about-products]')];
    this.tabs.forEach((tab,index) => {
      const select = () => this.select(tab.dataset.aboutTopic);
      tab.addEventListener('pointerenter', e => { if(e.pointerType === 'mouse') select(); }, options);
      tab.addEventListener('click', select, options);
      tab.addEventListener('focus', select, options);
      tab.addEventListener('keydown', e => {
        const next = {ArrowDown:(index+1)%this.tabs.length,ArrowUp:(index+this.tabs.length-1)%this.tabs.length,Home:0,End:this.tabs.length-1}[e.key];
        if(next !== undefined) { e.preventDefault(); this.tabs[next].focus(); }
      }, options);
    });
    this.querySelectorAll('[data-about-jump]').forEach(card => card.addEventListener('click', () => this.tabs.find(t => t.dataset.aboutTopic === card.dataset.aboutJump)?.focus(), options));
    this.addEventListener('keydown', e => {
      const accordion = this.closest('sisway-accordion');
      if(e.key === 'Escape' && accordion?.querySelector('details[open]')) {
        e.preventDefault(); e.stopPropagation();
        const summary = accordion.querySelector('summary'); summary.click(); summary.focus();
      }
    }, options);
    this.addEventListener('shopify:block:select', e => {
      const panel = e.target.closest('[data-about-products]');
      const group = this.closest('.m-header__desktop')?.siswaySurface;
      const owner = group && [...group.items].find(item => item.panel.contains(this));
      if(owner) group.open(owner);
      if(panel) this.select(panel.dataset.aboutProducts);
    }, options);
    this.select(this.dataset.defaultTopic || 'story',false);
  }
  select(key,animate=true) {
    if(this.active === key && animate) return;
    this.active = key;
    const stage = this.querySelector('.sisway-about-menu__stage');
    const width = stage.getBoundingClientRect().width;
    if (this.stageWidth !== width) stage.style.minHeight = '';
    this.stageWidth = width;
    const previousHeight = stage.getBoundingClientRect().height;
    this.tabs.forEach(tab => { const selected = tab.dataset.aboutTopic === key; tab.setAttribute('aria-selected',String(selected)); tab.tabIndex = selected ? 0 : -1; });
    this.panels.forEach(panel => {
      panel.motion?.cancel();
      const selected = panel.dataset.aboutProducts === key;
      panel.hidden = !selected; panel.inert = !selected;
      if(selected && animate && !matchMedia('(prefers-reduced-motion: reduce)').matches) panel.motion = panel.animate([{opacity:0},{opacity:1}],{duration:340,easing:'cubic-bezier(.22,1,.36,1)'});
    });
    stage.style.minHeight = `${Math.max(previousHeight, stage.scrollHeight)}px`;
  }
  disconnectedCallback() { this.controller?.abort(); this.panels?.forEach(panel => panel.motion?.cancel()); }
});

})();
